import { computed, type Ref, ref, watch } from "vue";

import { t } from "@/i18n.ts";

type ValidationState = "error" | "info" | "warning";

export interface UseInputValidationOptions {
  inputValue: Ref<string>;
  characterLimit?: number;
}

export interface UseInputValidationResult {
  inputValidation: Ref<ValidationState | undefined>;
  errorMessage: Ref<string>;
  shouldDisableInput: Ref<boolean>;
  characterLimit: Ref<number>;
}

const ESCAPED_CHAR_REGEX = /[<>]/;
const WHITESPACE_ONLY_REGEX = /^\s*$/;

export function useInputValidation(
  options: UseInputValidationOptions,
): UseInputValidationResult {
  const { inputValue, characterLimit: limitOption } = options;
  const characterLimit = ref(limitOption ?? 100);
  const hasEscapedCharacters = ref(false);
  const hasOnlyWhitespace = ref(false);

  watch(
    inputValue,
    (val) => {
      hasEscapedCharacters.value = ESCAPED_CHAR_REGEX.test(val);
      hasOnlyWhitespace.value = WHITESPACE_ONLY_REGEX.test(val);
    },
    { immediate: true },
  );

  // Should disable input if whitespace, escaped chars, or at forbidden lengths
  const shouldDisableInput = computed(() => {
    return (
      hasOnlyWhitespace.value ||
      hasEscapedCharacters.value ||
      inputValue.value.length >= characterLimit.value ||
      inputValue.value.length === 0
    );
  });

  const inputValidation = computed<ValidationState | undefined>(() => {
    if (
      hasEscapedCharacters.value ||
      inputValue.value.length >= characterLimit.value
    ) {
      return "error";
    }
    return undefined;
  });

  const errorMessage = computed(() => {
    if (inputValidation.value === "error") {
      if (hasEscapedCharacters.value) {
        return t("errors.inputValidation.escapeCharacters");
      }
      if (inputValue.value.length >= characterLimit.value) {
        if (characterLimit.value === 100) {
          return t("errors.inputValidation.characterLimitReached100");
        } else if (characterLimit.value === 1000) {
          return t("errors.inputValidation.characterLimitReached1000");
        }
      }
    }
    return "";
  });

  return {
    characterLimit,
    inputValidation,
    errorMessage,
    shouldDisableInput,
  };
}
